import { EyeIcon, EyeSlashIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { accountTypes } from 'config'
import { useEffect, useState } from 'react'
import { getAdminConfig, setAdminConfig } from 'services'
import { ButtonGroup, Modal, Tooltip } from 'stories/components'
import { LoanDetailsFieldConfig } from 'stories/layouts/FloaterTabs/loanDetails.constants'
import { RenderInput } from 'utils/RenderInput'

import { FloatTabs, pageUrls, sortedPageUrlKeys } from './constants'
import { LoanDetailsFieldsConfig } from './LoanDetailsFieldsConfig'

export const FloaterTabsConfig = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [data, setData] = useState<Record<string, any>>({})
  const [tab, setTab] = useState<string>('loanDetail')
  const [accountType, setAccountType] = useState<Record<string, boolean>>({})
  const [selectedPage, setSelectedPage] = useState('')
  const [editableAccTypes, setEditableAccTypes] = useState({})

  const initData = async () => {
    setIsLoading(true)
    const res = await getAdminConfig('floaterTabs')
    setData(res)
    setAccountType(res[tab]?.accountTypes || {})
    setIsLoading(false)
  }

  useEffect(() => {
    initData()
  }, [])

  useEffect(() => {
    setAccountType(data[tab]?.accountTypes || {})
  }, [tab])

  const onChange = async (key: string, subKey: string, value: any) => {
    if (isLoading) return
    const temp = cloneDeep(data)
    if (!temp[tab]) temp[tab] = {}
    if (!temp[tab][key]) temp[tab][key] = {}
    temp[tab][key][subKey] = value
    setData(temp)

    setIsLoading(true)
    await setAdminConfig('floaterTabs', temp)
    setIsLoading(false)
  }

  const onBlurAccountType = async () => {
    if (isLoading) return
    const temp = cloneDeep(data)
    if (!temp[tab]) temp[tab] = {}
    temp[tab].accountTypes = accountType
    setData(temp)

    setIsLoading(true)
    await setAdminConfig('floaterTabs', temp)
    setIsLoading(false)
  }

  const onChangeFieldConfig = async (fieldConfig: LoanDetailsFieldConfig) => {
    if (isLoading) return
    const temp = cloneDeep(data)
    if (!temp.loanDetail) temp.loanDetail = {}
    temp.loanDetail.fieldConfig = fieldConfig
    setData(temp)
    setIsLoading(true)
    await setAdminConfig('floaterTabs', temp)
    setIsLoading(false)
  }

  return (
    <div className="relative px-3 py-1.5">
      <LayoutLoading show={isLoading} />
      <ButtonGroup title={FloatTabs} value={tab} onChange={setTab} />

      <div className="p-2">
        <RenderInput
          input={{
            inputType: 'multiselect',
            options: accountTypes,
            title: 'Visible Account Types',
            value: accountType,
            defaultSelected: false,
          }}
          Key="accountType"
          onChange={(key: string, value: any) => setAccountType(value)}
          onBlur={onBlurAccountType}
        />
      </div>

      <div className="grid grid-cols-3 gap-x-2">
        {sortedPageUrlKeys.map((key, index) => {
          return (
            <div className="col-span-1" key={key}>
              <div className="flex gap-2 items-center p-2">
                <span className={`flex-1 ${!data[tab]?.[key]?.visible ? 'line-through opacity-50' : ''}`}>
                  {index + 1}. {pageUrls[key].title}
                </span>
                <Tooltip message="Show / Hide">
                  <span
                    className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
                    onClick={() => onChange(key, 'visible', !data[tab]?.[key]?.visible)}
                  >
                    {data[tab]?.[key]?.visible ? (
                      <EyeIcon className="w-4 h-4"></EyeIcon>
                    ) : (
                      <EyeSlashIcon className="text-gray-300 w-4 h-4"></EyeSlashIcon>
                    )}
                  </span>
                </Tooltip>
                <Tooltip message="Editable / View Only">
                  <span
                    className="p-1 hover-shadow1 cursor-pointer"
                    onClick={() => {
                      setSelectedPage(key)
                      setEditableAccTypes(data[tab]?.[key]?.editableAccTypes || {})
                    }}
                  >
                    {tab === 'documents' && <PencilSquareIcon className={`w-4 h-4`} />}
                  </span>
                </Tooltip>
              </div>
            </div>
          )
        })}
      </div>

      {tab === 'loanDetail' && (
        <LoanDetailsFieldsConfig
          value={data.loanDetail?.fieldConfig}
          onChange={onChangeFieldConfig}
          disabled={isLoading}
        />
      )}

      {selectedPage && (
        <Modal
          title={`${pageUrls[selectedPage].title} Page`}
          isOpen
          onClose={() => setSelectedPage('')}
          onOk={() => {
            onChange(selectedPage, 'editableAccTypes', editableAccTypes)
            setSelectedPage('')
          }}
        >
          <div className="w-[800px] h-[500px]">
            <RenderInput
              input={{
                inputType: 'multiselect',
                options: accountTypes,
                title: 'Editable Account Types',
                value: editableAccTypes,
                defaultSelected: false,
              }}
              Key="editableAccTypes"
              onChange={(key: string, value: any) => setEditableAccTypes(value)}
            />
          </div>
        </Modal>
      )}
    </div>
  )
}
